import React, { forwardRef, ReactNode } from 'react';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import LoadingButton from '../LoadingButton';
import { Link } from 'react-router';

export type ButtonStyle = 'default' | 'link' | 'primary-link' | 'highlighted' | 'ghost' | 'premium';

export type ButtonProps = {
	onClick?: (e?: any) => void;
	text?: string | ReactNode;
	width?: string;
	iconRight?: ReactNode;
	iconLeft?: ReactNode;
	size?: 'small' | 'medium' | 'large' | 'icon' | 'topbar';
	style?: ButtonStyle;
	type?: 'button' | 'submit' | 'reset';
	children?: ReactNode;
	asChild?: boolean;
	isActive?: boolean;
	disabled?: boolean;
	className?: string;
	form?: string;
	isLoading?: boolean;
	href?: string;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const basePadding = {
	small: 'px-3 py-1',
	medium: 'px-5 py-3',
	large: 'px-20 py-3',
	topbar: 'px-2 py-1 h-11',
	icon: 'rounded-full p-2 h-12 w-12',
};

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
	(
		{
			onClick,
			text,
			iconRight,
			iconLeft,
			width,
			size = 'medium',
			style = 'default',
			type = 'button',
			children,
			asChild = false,
			isActive = false,
			disabled = false,
			form,
			className,
			isLoading = false,
			href,
			...rest
		},
		ref,
	) => {
		const buttonStyles = twMerge(
			clsx(
				'rounded-[50px] border flex justify-center items-center gap-2 transition-all duration-300 ease-in-out group first-letter-uppercase whitespace-nowrap min-w-max',
				disabled ? 'cursor-not-allowed' : 'cursor-pointer',
				width,
				basePadding[size],
				{
					'bg-primary text-inverse border-transparent hover:bg-secondary-ultra-light hover:text-secondary hover:border-transparent':
						style === 'highlighted' && !disabled,
					'bg-primary text-inverse border-transparent opacity-30':
						style === 'highlighted' && disabled,
					'bg-transparent text-cuaternary border-transparent hover:text-primary':
						style === 'link' && !disabled,
					'bg-transparent text-primary border-transparent font-semibold':
						style === 'primary-link' && !disabled,
					'bg-light border-tertiary hover:bg-secondary-ultra-light hover:text-secondary hover:border-transparent':
						style === 'default' && !(isActive && style === 'default') && !disabled,
					'bg-light border-tertiary opacity-50': style === 'default' && disabled,
					'bg-transparent text-secondary border-transparent px-0 py-0':
						style === 'ghost' && !disabled,
					'bg-secondary-light text-white border-fill-secondary-light':
						isActive && style === 'default',
					'border-none bg-light': style === 'premium',
					'opacity-30':
						(style === 'link' || style === 'primary-link' || style === 'ghost') && disabled,
				},
				className,
			),
		);

		const renderButtonContent = () => (
			<>
				{iconLeft && text && <span className='inline-block'>{iconLeft}</span>}
				<span className='inline-block first-letter-uppercase'>
					{text ? text : iconLeft ? iconLeft : iconRight ? iconRight : children}
				</span>
				{iconRight && text && <span className='inline-block'>{iconRight}</span>}
			</>
		);

		if (href) {
			return (
				<Link
					to={href}
					className={buttonStyles}
					ref={ref as React.Ref<HTMLAnchorElement>}
				>
					{renderButtonContent()}
				</Link>
			);
		}

		if (size === 'icon') {
			return (
				<button
					ref={ref as React.Ref<HTMLButtonElement>}
					onClick={onClick}
					className={buttonStyles}
					type={type}
					disabled={disabled}
					form={form}
					{...rest}
				>
					{iconLeft}
				</button>
			);
		}

		if (isLoading) return <LoadingButton />;

		if (style === 'premium') {
			return (
				<div
					className={clsx(
						'border-premium-gradient rounded-[50px] flex items-center justify-center p-[2px]',
						disabled && 'pointer-events-none opacity-40 cursor-not-allowed',
						width,
					)}
				>
					<button
						ref={ref as React.Ref<HTMLButtonElement>}
						onClick={onClick}
						type={type}
						disabled={disabled}
						form={form}
						{...rest}
						className='bg-light cursor-pointer rounded-[50px] flex justify-center items-center gap-2 font-medium first-letter-uppercase whitespace-nowrap min-w-max'
					>
						{renderButtonContent()}
					</button>
				</div>
			);
		}

		return (
			<button
				ref={ref as React.Ref<HTMLButtonElement>}
				onClick={onClick}
				className={buttonStyles}
				type={type}
				disabled={disabled}
				form={form}
				{...rest}
			>
				{renderButtonContent()}
			</button>
		);
	},
);

export default Button;
